import http from '@/services/http';
import {
	DASHBOARD_CITIES_URL,
	DASHBOARD_API_KEYS_URL,
	DASHBOARD_GOOGLE_REQUESTS_URL,
	DASHBOARD_GOOGLE_COSTS_URL,
	DASHBOARD_INTEREST_URL,
	DASHBOARD_INTEREST_COUNT_URL,
} from './urls';

// Master-only overview endpoints. Unlike the other controllers these take no
// cityId — the whole point is to see every city without switching the header
// city selector.
class DashboardController {
	getCitiesOverview = async () => {
		let data;

		try {
			const res = await http.get(DASHBOARD_CITIES_URL);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return {
			cities: data.cities,
			orphans: data.orphans,
			totals: data.totals,
		};
	};

	getApiKeys = async () => {
		let data;

		try {
			const res = await http.get(DASHBOARD_API_KEYS_URL);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return {
			keys: data.keys,
			// Every city and shop (ids and names), for the form that creates a key.
			cities: data.cities,
			shops: data.shops,
			testCityId: data.testCityId,
		};
	};

	// payload: { mode: 'test' | 'real', kind: 'shop' | 'company', name, cityId,
	// shopId, newShopName }. Answers with the new key and, when a test shop was
	// made with it, that shop's id and login.
	createApiKey = async payload => {
		let data;

		try {
			const res = await http.post(DASHBOARD_API_KEYS_URL, payload);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return {
			key: data.key,
			testShop: data.testShop,
		};
	};

	// Disables a key (blocked: true) or switches it back on.
	setApiKeyBlocked = async (id, blocked) => {
		let data;

		try {
			const res = await http.put(`${DASHBOARD_API_KEYS_URL}/${id}`, { blocked });
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return { ok: true };
	};

	deleteApiKey = async id => {
		let data;

		try {
			const res = await http.delete(`${DASHBOARD_API_KEYS_URL}/${id}`);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return { ok: true };
	};

	getTopGoogleRequests = async () => {
		let data;

		try {
			const res = await http.get(DASHBOARD_GOOGLE_REQUESTS_URL);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return {
			requests: data.requests,
		};
	};

	// The estimated Google bill of one month ('YYYY-MM'; empty = this month) and
	// each city's share. The SWR key is [url, month].
	getGoogleCosts = async ([, month]) => {
		let data;

		try {
			const res = await http.get(DASHBOARD_GOOGLE_COSTS_URL, month ? { month } : undefined);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return data;
	};

	// Requests left on the public website's interest form.
	getInterestRequests = async () => {
		let data;

		try {
			const res = await http.get(DASHBOARD_INTEREST_URL);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return {
			requests: data.requests,
		};
	};

	// How many interest requests are still new, and the newest of them.
	getInterestRequestsCount = async () => {
		let data;

		try {
			const res = await http.get(DASHBOARD_INTEREST_COUNT_URL);
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return { newCount: data.newCount, latestNew: data.latestNew };
	};

	// status: 'new' | 'called'
	updateInterestRequest = async (id, status) => {
		let data;

		try {
			const res = await http.put(`${DASHBOARD_INTEREST_URL}/${id}`, { status });
			data = res?.data;
		} catch (error) {
			console.log(error);
			return { error: true };
		}

		if (!data || data?.code) {
			return { error: data?.error ?? true, code: data?.code };
		}

		return { ok: true };
	};
}

const dashboardController = new DashboardController();

export default dashboardController;
